import React, { useMemo, useRef, useState } from 'react';
import Button from '../../../components/common/Button/Button';
import ClearFiltersButton from '../../../components/common/ClearFiltersButton/ClearFiltersButton';
import DataTable from '../../../components/common/DataTable/DataTable';
import EmptyState from '../../../components/common/EmptyState/EmptyState';
import FilterBar from '../../../components/common/FilterBar/FilterBar';
import FilterGroup from '../../../components/common/FilterGroup/FilterGroup';
import Loading from '../../../components/common/Loading/Loading';
import Pagination from '../../../components/common/Pagination/Pagination';
import RecordCount from '../../../components/common/RecordCount/RecordCount';
import SectionTitle from '../../../components/common/SectionTitle/SectionTitle';
import SingleSelectFilter from '../../../components/common/SingleSelectFilter/SingleSelectFilter';
import { queryFilter, useQueryFilters } from '../../../hooks/useQueryFilters';
import { MAIN_GOAL_LABELS } from '../../../constants';
import { StudentImportBackupService } from '../../../services/StudentImportBackupService';
import { TrainingImportService } from '../../../services/TrainingImportService';
import { TRAINING_CATEGORY_LABELS } from '../../../types/training';
import type { PaginationInfo } from '../../../types';
import type { MainGoal, TrainingLocation } from '../../../types/registration';
import { studentImportBackupFileName } from '../../../utils/studentImportBackupSql';
import {
  markExistingWorkoutImports,
  parseWorkoutHistoryImport,
  type WorkoutImportRow,
  type WorkoutImportStatus,
} from '../../../utils/workoutHistoryImport';
import StudentFollowUpImport from './StudentFollowUpImport';
import StudentNotesImport from './StudentNotesImport';
import StudentWorkoutHistoryImport from './StudentWorkoutHistoryImport';
import styles from './ImportacoesTab.module.css';

const PAGE_SIZE = 20;
const STATUS_FILTERS = ['all', 'ready', 'duplicate', 'blocked'] as const;
type ImportStatusFilter = (typeof STATUS_FILTERS)[number];

const FILTER_SCHEMA = {
  page: queryFilter.integer(1, { min: 1, param: 'importPage' }),
  status: queryFilter.enum<ImportStatusFilter>('all', STATUS_FILTERS, { param: 'importStatus' }),
};

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'blocked', label: 'Fora do padrão' },
  { value: 'ready', label: 'Prontos para importar' },
  { value: 'duplicate', label: 'Já cadastrados' },
];
const GRID_TEMPLATE = '40px minmax(220px, 2fr) minmax(120px, 1fr) minmax(140px, 1.1fr) minmax(150px, 1.2fr) 90px 120px 90px minmax(160px, 1.2fr) minmax(180px, 1.3fr)';
const HEADERS = ['', 'Nome', 'Nível', 'Categoria', 'Objetivo', 'Mês', 'Frequência', 'Duração', 'Local', 'Situação'];

const LEVEL_LABELS: Record<string, string> = {
  beginner: 'Iniciante',
  intermediate: 'Intermediário',
  advanced: 'Avançado',
  very_advanced: 'Muito Avançado',
};

const LOCATION_LABELS: Record<TrainingLocation, string> = {
  full_gym: 'Academia completa',
  home_condo_gym: 'Casa / academia do condomínio',
};

const readErrorMessage = (error: unknown, fallback: string): string => (
  error instanceof Error && error.message.trim().length > 0 ? error.message : fallback
);

const downloadSqlFile = (sql: string, fileName: string) => {
  const url = URL.createObjectURL(new Blob([sql], { type: 'application/sql;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const formatList = (values: string[]): string => (values.length > 0 ? values.join(', ') : '—');

const formatObjective = (objective: MainGoal[]): string => (
  formatList(objective.map((value) => MAIN_GOAL_LABELS[value] ?? value))
);

const situationLabel = (row: WorkoutImportRow): string => {
  if (row.status === 'duplicate') {
    return 'Já cadastrado';
  }
  if (row.status === 'blocked') {
    return row.reason ?? 'Fora da importação';
  }
  return 'Novo';
};

const nameClassName = (status: WorkoutImportStatus): string | undefined => {
  if (status === 'duplicate') {
    return styles.duplicateName;
  }
  if (status === 'blocked') {
    return styles.blockedName;
  }
  return undefined;
};

const ImportacoesTab: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<'choose' | 'workouts' | 'history' | 'fup' | 'notes'>('choose');
  const [rows, setRows] = useState<WorkoutImportRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [backingUp, setBackingUp] = useState(false);

  const { filters, setFilter, resetFilters } = useQueryFilters(FILTER_SCHEMA, { pageKey: 'page' });
  const selectedCount = rows.filter((row) => selectedIds[row.id] && row.status === 'ready').length;
  const readyCount = rows.filter((row) => row.status === 'ready').length;
  const duplicateCount = rows.filter((row) => row.status === 'duplicate').length;
  const blockedCount = rows.filter((row) => row.status === 'blocked').length;
  const filteredRows = filters.status === 'all'
    ? rows
    : rows.filter((row) => row.status === filters.status);

  const pagination = useMemo<PaginationInfo>(() => {
    const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
    const currentPage = Math.min(filters.page, totalPages);
    return {
      page: currentPage,
      pageSize: PAGE_SIZE,
      totalRecords: filteredRows.length,
      totalPages,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1,
    };
  }, [filters.page, filteredRows.length]);

  const visibleRows = filteredRows.slice((pagination.page - 1) * PAGE_SIZE, pagination.page * PAGE_SIZE);

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBackup = async () => {
    setBackingUp(true);
    setError(null);
    setSuccess(null);

    try {
      const sql = await StudentImportBackupService.buildSql();
      downloadSqlFile(sql, studentImportBackupFileName(new Date()));
    } catch (caught) {
      setError(readErrorMessage(caught, 'Não foi possível gerar o backup.'));
    } finally {
      setBackingUp(false);
    }
  };

  const handleChooseWorkouts = () => {
    setStep('workouts');
    setRows([]);
    setSelectedIds({});
    setError(null);
    setSuccess(null);
    setFilter('page', 1);
  };

  const handleFile = async (file: File) => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    setRows([]);
    setSelectedIds({});
    setFilter('page', 1);

    try {
      const text = await file.text();
      let payload: unknown;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new Error('Envie um JSON de histórico de treinos (workout-history).');
      }

      const parsed = parseWorkoutHistoryImport(payload);
      const existingNames = await TrainingImportService.matchNames(
        parsed.filter((row) => row.status === 'ready').map((row) => row.name),
      );
      const marked = markExistingWorkoutImports(parsed, existingNames);
      setRows(marked);
      setSelectedIds(Object.fromEntries(marked.map((row) => [row.id, row.status === 'ready'])));
    } catch (caught) {
      setError(readErrorMessage(caught, 'Não foi possível ler o arquivo.'));
    } finally {
      setLoading(false);
      resetFileInput();
    }
  };

  const toggleRow = (row: WorkoutImportRow) => {
    if (row.status !== 'ready') {
      return;
    }
    setSelectedIds((current) => ({ ...current, [row.id]: !current[row.id] }));
  };

  const handleImport = async () => {
    const selected = rows.filter((row) => selectedIds[row.id] && row.status === 'ready');
    if (selected.length === 0) {
      return;
    }

    setImporting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await TrainingImportService.commit(selected);
      const existingNames = [
        ...rows.filter((row) => row.status === 'duplicate').map((row) => row.name),
        ...selected.map((row) => row.name),
      ];
      const marked = markExistingWorkoutImports(
        rows.map((row) => (row.status === 'duplicate' ? { ...row, status: 'ready' as const, reason: null } : row)),
        existingNames,
      );
      setRows(marked);
      setSelectedIds(Object.fromEntries(marked.map((row) => [row.id, false])));
      setSuccess(`${result.importedCount} treino${result.importedCount === 1 ? '' : 's'} importado${result.importedCount === 1 ? '' : 's'}.`);
    } catch (caught) {
      setError(readErrorMessage(caught, 'Não foi possível importar os treinos.'));
    } finally {
      setImporting(false);
    }
  };

  if (step === 'choose') {
    return (
      <div className={styles.intro}>
        <SectionTitle as="h2" text="Importações" />
        <div className={`${styles.actions} ${styles.importChoices}`}>
          <Button type="button" variant="outline" size="small" onClick={handleChooseWorkouts}>
            Importar treinos
          </Button>
          <Button type="button" variant="outline" size="small" onClick={() => setStep('history')}>
            Importar histórico de treinos
          </Button>
          <Button type="button" variant="outline" size="small" onClick={() => setStep('fup')}>
            Importar histórico de FUP
          </Button>
          <Button type="button" variant="outline" size="small" onClick={() => setStep('notes')}>
            Importar histórico de observações
          </Button>
          <Button type="button" variant="outline" size="small" onClick={() => void handleBackup()} disabled={backingUp}>
            {backingUp ? 'Gerando backup...' : 'Baixar backup'}
          </Button>
        </div>
        {error && <p className={`${styles.message} ${styles.error}`}>{error}</p>}
      </div>
    );
  }

  if (step === 'history') {
    return (
      <StudentWorkoutHistoryImport onBack={() => setStep('choose')} />
    );
  }

  if (step === 'fup') {
    return (
      <StudentFollowUpImport onBack={() => setStep('choose')} />
    );
  }

  if (step === 'notes') {
    return (
      <StudentNotesImport onBack={() => setStep('choose')} />
    );
  }

  return (
    <div>
      <div className={styles.toolbar}>
        <SectionTitle as="h2" text="Importar treinos" />
        <div className={styles.actions}>
          <Button type="button" variant="outline" size="medium" onClick={() => fileInputRef.current?.click()} disabled={loading || importing}>
            Escolher JSON
          </Button>
          <Button type="button" size="medium" onClick={handleImport} disabled={loading || importing || selectedCount === 0}>
            {importing ? 'Importando...' : `Importar selecionados (${selectedCount})`}
          </Button>
          <input
            ref={fileInputRef}
            className={styles.fileInput}
            type="file"
            accept="application/json,.json"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                void handleFile(file);
              }
            }}
          />
        </div>
      </div>

      {error && <p className={`${styles.message} ${styles.error}`}>{error}</p>}
      {success && <p className={`${styles.message} ${styles.success}`}>{success}</p>}
      {loading && <Loading />}

      {!loading && rows.length === 0 && !error && (
        <EmptyState message="Escolha um JSON workout-history para ver a prévia." icon="upload_file" showImage={false} />
      )}

      {!loading && rows.length > 0 && (
        <>
          <p className={styles.summary}>
            {`${readyCount} pronto${readyCount === 1 ? '' : 's'} para importar, ${duplicateCount} já cadastrado${duplicateCount === 1 ? '' : 's'}, ${blockedCount} fora do padrão.`}
          </p>
          <FilterBar gap="sm" pushFirstChild className={styles.filters}>
            <RecordCount
              count={`${filteredRows.length} ${filteredRows.length === 1 ? 'treino' : 'treinos'}`}
            />
            <FilterGroup>
              <SingleSelectFilter
                value={filters.status}
                onChange={(value) => setFilter('status', value as ImportStatusFilter)}
                options={STATUS_FILTER_OPTIONS}
                placeholder="Situação"
              />
            </FilterGroup>
            {filters.status !== 'all' && (
              <ClearFiltersButton onClick={() => resetFilters()} />
            )}
          </FilterBar>
          {filteredRows.length === 0 ? (
            <EmptyState
              message={filters.status === 'blocked' ? 'Nenhum treino fora do padrão.' : 'Nenhum treino nesta situação.'}
              icon="filter_list"
              showImage={false}
            />
          ) : (
            <>
              <div className={styles.tableScroll}>
                <DataTable headers={HEADERS} gridTemplate={GRID_TEMPLATE} lastColumnIsActions={false} borderedRows>
                  {visibleRows.map((row) => (
                    <DataTable.Row key={row.id}>
                      <input
                        type="checkbox"
                        checked={Boolean(selectedIds[row.id]) && row.status === 'ready'}
                        disabled={row.status !== 'ready' || importing}
                        aria-label={`Selecionar ${row.name}`}
                        onChange={() => toggleRow(row)}
                      />
                      <span className={nameClassName(row.status)}>{row.name}</span>
                      <span>{formatList(row.levels.map((level) => LEVEL_LABELS[level] ?? level))}</span>
                      <span>{row.category ? TRAINING_CATEGORY_LABELS[row.category] : '—'}</span>
                      <span>{formatObjective(row.objective)}</span>
                      <span>{row.monthIndex ? `Mês ${row.monthIndex}` : '—'}</span>
                      <span>{row.sessionsPerWeek ? `${row.sessionsPerWeek}x por semana` : '—'}</span>
                      <span>{row.durationMinutes ? `${row.durationMinutes} min` : '—'}</span>
                      <span>{row.location ? LOCATION_LABELS[row.location] : '—'}</span>
                      <span>{situationLabel(row)}</span>
                    </DataTable.Row>
                  ))}
                </DataTable>
              </div>
              {pagination.totalPages > 1 && (
                <div className={styles.pagination}>
                  <Pagination pagination={pagination} loading={importing} onPageChange={(nextPage) => setFilter('page', nextPage)} />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ImportacoesTab;
